/**
 * Microsoft Dataverse Web API client for model-driven Power Apps.
 *
 * This client intentionally uses only the documented AppModule table,
 * AddAppComponents/RemoveAppComponents actions, RetrieveAppComponents and
 * ValidateApp functions, PublishXml action, and appmoduleroles_association.
 */

import { requestWithRetry, readResponseBody } from "../utils/http.js";
import type { AuthProvider } from "../auth/types.js";
import {
  ApiError,
  AuthenticationError,
  AuthorizationError,
  RateLimitError,
  wrapApiCall,
} from "../utils/errors.js";
import {
  sanitizeErrorMessage,
  validateDataverseUrl,
  validateLogicalName,
  validateRowId,
} from "../utils/security.js";

const API_VERSION = "v9.2";

/** Default icon documented by Microsoft for newly-created AppModule records. */
export const DEFAULT_MODEL_DRIVEN_APP_ICON_ID = "953b9fac-1e5e-e611-80d6-00155ded156f";

const APP_SUMMARY_SELECT = [
  "appmoduleid",
  "name",
  "uniquename",
  "description",
  "clienttype",
  "formfactor",
  "navigationtype",
  "appmoduleversion",
  "url",
  "webresourceid",
  "componentstate",
  "ismanaged",
  "isfeatured",
  "createdon",
  "modifiedon",
  "publishedon",
].join(",");

const APP_DEFINITION_SELECT = [
  APP_SUMMARY_SELECT,
  "descriptor",
  "appgraph",
  "configxml",
  "appmodulexmlmanaged",
].join(",");

export interface ModelDrivenApiConfig {
  authProvider: AuthProvider;
  environmentUrl: string;
}

export interface ModelDrivenApp {
  appmoduleid: string;
  name: string;
  uniquename: string;
  description?: string;
  clienttype?: number;
  formfactor?: number;
  navigationtype?: number;
  appmoduleversion?: string;
  url?: string;
  webresourceid?: string;
  componentstate?: number;
  ismanaged?: boolean;
  isfeatured?: boolean;
  createdon?: string;
  modifiedon?: string;
  publishedon?: string;
  descriptor?: string;
  appgraph?: string;
  configxml?: string;
  appmodulexmlmanaged?: string;
  appmoduleroles_association?: ModelDrivenSecurityRole[];
  [key: string]: unknown;
}

export interface CreateModelDrivenAppOptions {
  name: string;
  uniqueName: string;
  description?: string;
  webResourceId?: string;
  /** Add the new AppModule directly to this unmanaged solution. */
  solutionUniqueName?: string;
}

export interface CreateModelDrivenAppResult {
  appId: string;
  entityUri?: string;
  app?: ModelDrivenApp;
}

export interface UpdateModelDrivenAppOptions {
  name?: string;
  description?: string | null;
  url?: string | null;
  appModuleVersion?: string;
  isFeatured?: boolean;
  navigationType?: number;
  formFactor?: number;
  solutionUniqueName?: string;
}

/**
 * A Dataverse entity reference accepted by AddAppComponents and
 * RemoveAppComponents. Most primary keys are `${entityType}id`; callers can
 * provide idProperty for exceptions such as systemform.formid.
 */
export interface ModelDrivenComponentInput {
  entityType: string;
  id: string;
  idProperty?: string;
}

export type ModelDrivenAppComponent = Record<string, unknown>;

export interface ModelDrivenAppValidationIssue {
  ComponentId?: string;
  ComponentType?: number;
  ComponentName?: string;
  Description?: string;
  ErrorType?: number | string;
  Message?: string;
  [key: string]: unknown;
}

export interface ModelDrivenAppValidation {
  ValidationSuccess: boolean;
  ValidationIssueList: ModelDrivenAppValidationIssue[];
}

export interface ModelDrivenSecurityRole {
  roleid: string;
  name?: string;
  businessunitid?: string;
  [key: string]: unknown;
}

const PRIMARY_ID_OVERRIDES: Readonly<Record<string, string>> = {
  systemform: "formid",
};

function validateUniqueName(value: string, label: string): string {
  if (!/^[A-Za-z][A-Za-z0-9_]{0,99}$/.test(value)) {
    throw new ApiError(
      `${label} must start with a letter and contain only letters, numbers, and underscores (max 100 characters)`,
      400,
    );
  }
  return value;
}

function parseEntityId(entityUri: string | string[] | undefined): string | undefined {
  const raw = Array.isArray(entityUri) ? entityUri[0] : entityUri;
  if (!raw) return undefined;
  const match = raw.match(/appmodules\(([0-9a-f-]{36})\)/i);
  return match?.[1];
}

/** Client for the supported model-driven AppModule lifecycle. */
export class ModelDrivenApi {
  private readonly authProvider: AuthProvider;
  private environmentUrl: string;

  constructor(config: ModelDrivenApiConfig) {
    this.authProvider = config.authProvider;
    this.environmentUrl = validateDataverseUrl(config.environmentUrl);
  }

  setEnvironmentUrl(url: string): void {
    this.environmentUrl = validateDataverseUrl(url);
  }

  private get origin(): string {
    return `https://${this.environmentUrl}`;
  }

  private async getAuthHeaders(): Promise<Record<string, string>> {
    const token = await this.authProvider.getTokenForResource(this.origin);
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json; charset=utf-8",
      "OData-MaxVersion": "4.0",
      "OData-Version": "4.0",
      Accept: "application/json",
    };
  }

  private buildUrl(path: string, query?: string): string {
    const base = `${this.origin}/api/data/${API_VERSION}/${path}`;
    return query ? `${base}?${query}` : base;
  }

  private async handleResponse<T>(
    response: Awaited<ReturnType<typeof requestWithRetry>>,
    context: string,
  ): Promise<T> {
    if (response.statusCode === 429) {
      const retryAfter = response.headers["retry-after"];
      await response.body.text().catch(() => undefined);
      const seconds = retryAfter
        ? Number(Array.isArray(retryAfter) ? retryAfter[0] : retryAfter)
        : NaN;
      throw new RateLimitError(Number.isFinite(seconds) ? seconds * 1000 : undefined);
    }

    const { statusCode, text } = await readResponseBody(response, context);

    if (statusCode === 401) {
      throw new AuthenticationError(`${context}: Dataverse authentication failed`);
    }
    if (statusCode === 403) {
      throw new AuthorizationError(
        `${context}: access denied. This operation requires the corresponding privilege on the Model-driven App table (normally System Administrator or System Customizer; delete specifically requires Delete privilege)`,
      );
    }
    if (statusCode >= 400) {
      let details: string;
      try {
        details = JSON.stringify(JSON.parse(text));
      } catch {
        details = text;
      }
      throw new ApiError(
        `${context}: Dataverse API error`,
        statusCode,
        sanitizeErrorMessage(details),
      );
    }

    if (!text) return {} as T;
    try {
      return JSON.parse(text) as T;
    } catch (error) {
      throw new ApiError(`${context}: invalid JSON response`, statusCode, {
        parseError: String(error),
        responsePreview: sanitizeErrorMessage(text.slice(0, 200)),
      });
    }
  }

  async listApps(options: { unpublished?: boolean; top?: number } = {}): Promise<ModelDrivenApp[]> {
    const top = Math.min(Math.max(options.top ?? 100, 1), 250);
    const path = options.unpublished
      ? "appmodules/Microsoft.Dynamics.CRM.RetrieveUnpublishedMultiple()"
      : "appmodules";
    const query = `$select=${APP_SUMMARY_SELECT}&$orderby=name asc&$top=${top}`;
    const url = this.buildUrl(path, query);
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "GET", headers });
      const data = await this.handleResponse<{ value?: ModelDrivenApp[] }>(
        response,
        `List ${options.unpublished ? "unpublished " : ""}model-driven apps`,
      );
      return Array.isArray(data.value) ? data.value : [];
    }, "List model-driven apps");
  }

  async getApp(
    appId: string,
    options: { unpublished?: boolean; includeDefinition?: boolean } = {},
  ): Promise<ModelDrivenApp> {
    const safeAppId = validateRowId(appId);
    const path = options.unpublished
      ? `appmodules(${safeAppId})/Microsoft.Dynamics.CRM.RetrieveUnpublished()`
      : `appmodules(${safeAppId})`;
    const select = options.includeDefinition ? APP_DEFINITION_SELECT : APP_SUMMARY_SELECT;
    const url = this.buildUrl(path, `$select=${select}`);
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "GET", headers });
      return this.handleResponse<ModelDrivenApp>(response, `Get model-driven app ${safeAppId}`);
    }, `Get model-driven app ${safeAppId}`);
  }

  async createApp(options: CreateModelDrivenAppOptions): Promise<CreateModelDrivenAppResult> {
    const name = options.name.trim();
    if (!name || name.length > 100) {
      throw new ApiError("App name must be between 1 and 100 characters", 400);
    }
    const uniqueName = validateUniqueName(options.uniqueName, "App uniqueName");
    const webResourceId = validateRowId(options.webResourceId ?? DEFAULT_MODEL_DRIVEN_APP_ICON_ID);
    if (options.description !== undefined && options.description.length > 2000) {
      throw new ApiError("App description must not exceed 2000 characters", 400);
    }

    const headers = await this.getAuthHeaders();
    if (options.solutionUniqueName) {
      headers["MSCRM.SolutionUniqueName"] = validateUniqueName(
        options.solutionUniqueName,
        "Solution unique name",
      );
    }

    const body: Record<string, unknown> = {
      name,
      uniquename: uniqueName,
      webresourceid: webResourceId,
    };
    if (options.description !== undefined) body.description = options.description;

    return wrapApiCall(async () => {
      const response = await requestWithRetry(this.buildUrl("appmodules"), {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });
      const entityUriHeader = response.headers["odata-entityid"];
      const entityUri = Array.isArray(entityUriHeader) ? entityUriHeader[0] : entityUriHeader;
      const app = await this.handleResponse<ModelDrivenApp>(response, "Create model-driven app");
      const appId = app.appmoduleid ?? parseEntityId(entityUri);
      if (!appId) {
        throw new ApiError(
          "Create model-driven app succeeded but Dataverse returned no appmodule ID",
          502,
        );
      }
      return { appId, entityUri, ...(app.appmoduleid ? { app } : {}) };
    }, "Create model-driven app");
  }

  async updateApp(appId: string, options: UpdateModelDrivenAppOptions): Promise<void> {
    const safeAppId = validateRowId(appId);
    const body: Record<string, unknown> = {};

    if (options.name !== undefined) {
      const name = options.name.trim();
      if (!name || name.length > 100) {
        throw new ApiError("App name must be between 1 and 100 characters", 400);
      }
      body.name = name;
    }
    if (options.description !== undefined) {
      if (options.description !== null && options.description.length > 2000) {
        throw new ApiError("App description must not exceed 2000 characters", 400);
      }
      body.description = options.description;
    }
    if (options.url !== undefined) body.url = options.url;
    if (options.appModuleVersion !== undefined) body.appmoduleversion = options.appModuleVersion;
    if (options.isFeatured !== undefined) body.isfeatured = options.isFeatured;
    if (options.navigationType !== undefined) {
      if (options.navigationType !== 0 && options.navigationType !== 1) {
        throw new ApiError(
          "App navigationType must be 0 (single session) or 1 (multi session)",
          400,
        );
      }
      body.navigationtype = options.navigationType;
    }
    if (options.formFactor !== undefined) {
      if (
        !Number.isInteger(options.formFactor) ||
        options.formFactor < 1 ||
        options.formFactor > 8
      ) {
        throw new ApiError("App formFactor must be an integer from 1 through 8", 400);
      }
      body.formfactor = options.formFactor;
    }

    if (Object.keys(body).length === 0) {
      throw new ApiError("At least one model-driven app property must be provided", 400);
    }

    // Standard AppModule PATCH addresses the published set. A just-created
    // shell exists only in RetrieveUnpublished; PATCH would become an upsert
    // and fail with missing-required-field or duplicate-key errors. Detect that
    // state up front and give the caller the documented lifecycle ordering.
    try {
      await this.getApp(safeAppId);
    } catch (error) {
      if (error instanceof ApiError && error.statusCode === 404) {
        throw new ApiError(
          "Publish a newly-created model-driven app once before updating its properties",
          409,
        );
      }
      throw error;
    }

    const headers = await this.getAuthHeaders();
    headers["If-Match"] = "*";
    if (options.solutionUniqueName) {
      headers["MSCRM.SolutionUniqueName"] = validateUniqueName(
        options.solutionUniqueName,
        "Solution unique name",
      );
    }

    return wrapApiCall(async () => {
      const response = await requestWithRetry(this.buildUrl(`appmodules(${safeAppId})`), {
        method: "PATCH",
        headers,
        body: JSON.stringify(body),
      });
      await this.handleResponse<unknown>(response, `Update model-driven app ${safeAppId}`);
    }, `Update model-driven app ${safeAppId}`);
  }

  async deleteApp(appId: string): Promise<void> {
    const safeAppId = validateRowId(appId);
    const headers = await this.getAuthHeaders();
    return wrapApiCall(async () => {
      const response = await requestWithRetry(this.buildUrl(`appmodules(${safeAppId})`), {
        method: "DELETE",
        headers,
      });
      await this.handleResponse<unknown>(response, `Delete model-driven app ${safeAppId}`);
    }, `Delete model-driven app ${safeAppId}`);
  }

  private serializeComponents(components: ModelDrivenComponentInput[]): Record<string, unknown>[] {
    if (components.length === 0 || components.length > 100) {
      throw new ApiError("Provide between 1 and 100 app components", 400);
    }
    return components.map((component) => {
      const entityType = validateLogicalName(component.entityType);
      const id = validateRowId(component.id);
      const idProperty = validateLogicalName(
        component.idProperty ?? PRIMARY_ID_OVERRIDES[entityType] ?? `${entityType}id`,
      );
      return {
        [idProperty]: id,
        "@odata.type": `Microsoft.Dynamics.CRM.${entityType}`,
      };
    });
  }

  private async changeComponents(
    action: "AddAppComponents" | "RemoveAppComponents",
    appId: string,
    components: ModelDrivenComponentInput[],
  ): Promise<void> {
    const safeAppId = validateRowId(appId);
    const payload = {
      AppId: safeAppId,
      Components: this.serializeComponents(components),
    };
    const headers = await this.getAuthHeaders();
    return wrapApiCall(async () => {
      const response = await requestWithRetry(this.buildUrl(action), {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });
      await this.handleResponse<unknown>(response, `${action} for app ${safeAppId}`);
    }, `${action} for app ${safeAppId}`);
  }

  async addComponents(appId: string, components: ModelDrivenComponentInput[]): Promise<void> {
    return this.changeComponents("AddAppComponents", appId, components);
  }

  async removeComponents(appId: string, components: ModelDrivenComponentInput[]): Promise<void> {
    return this.changeComponents("RemoveAppComponents", appId, components);
  }

  async retrieveComponents(appId: string): Promise<ModelDrivenAppComponent[]> {
    const safeAppId = validateRowId(appId);
    const headers = await this.getAuthHeaders();
    const url = this.buildUrl(`RetrieveAppComponents(AppModuleId=${safeAppId})`);
    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "GET", headers });
      const data = await this.handleResponse<
        { value?: ModelDrivenAppComponent[] } | ModelDrivenAppComponent[]
      >(response, `Retrieve components for app ${safeAppId}`);
      if (Array.isArray(data)) return data;
      return Array.isArray(data.value) ? data.value : [];
    }, `Retrieve components for app ${safeAppId}`);
  }

  async validateApp(appId: string): Promise<ModelDrivenAppValidation> {
    const safeAppId = validateRowId(appId);
    const headers = await this.getAuthHeaders();
    const url = this.buildUrl(`ValidateApp(AppModuleId=${safeAppId})`);
    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "GET", headers });
      const data = await this.handleResponse<{
        AppValidationResponse?: ModelDrivenAppValidation;
      }>(response, `Validate model-driven app ${safeAppId}`);
      if (!data.AppValidationResponse) {
        throw new ApiError("ValidateApp returned no AppValidationResponse", 502);
      }
      return {
        ValidationSuccess: data.AppValidationResponse.ValidationSuccess,
        ValidationIssueList: Array.isArray(data.AppValidationResponse.ValidationIssueList)
          ? data.AppValidationResponse.ValidationIssueList
          : [],
      };
    }, `Validate model-driven app ${safeAppId}`);
  }

  async publishApp(appId: string): Promise<void> {
    const safeAppId = validateRowId(appId);
    const headers = await this.getAuthHeaders();
    const parameterXml =
      `<importexportxml><appmodules><appmodule>${safeAppId}</appmodule>` +
      "</appmodules></importexportxml>";
    return wrapApiCall(async () => {
      const response = await requestWithRetry(this.buildUrl("PublishXml"), {
        method: "POST",
        headers,
        body: JSON.stringify({ ParameterXml: parameterXml }),
      });
      await this.handleResponse<unknown>(response, `Publish model-driven app ${safeAppId}`);
    }, `Publish model-driven app ${safeAppId}`);
  }

  async listSecurityRoles(appId: string): Promise<ModelDrivenSecurityRole[]> {
    const safeAppId = validateRowId(appId);
    const query =
      "$select=appmoduleid,name&" + "$expand=appmoduleroles_association($select=roleid,name)";
    const headers = await this.getAuthHeaders();
    return wrapApiCall(async () => {
      const response = await requestWithRetry(this.buildUrl(`appmodules(${safeAppId})`, query), {
        method: "GET",
        headers,
      });
      const app = await this.handleResponse<ModelDrivenApp>(
        response,
        `List security roles for app ${safeAppId}`,
      );
      return Array.isArray(app.appmoduleroles_association) ? app.appmoduleroles_association : [];
    }, `List security roles for app ${safeAppId}`);
  }

  async associateSecurityRole(appId: string, roleId: string): Promise<void> {
    const safeAppId = validateRowId(appId);
    const safeRoleId = validateRowId(roleId);
    const headers = await this.getAuthHeaders();
    const roleUri = this.buildUrl(`roles(${safeRoleId})`);
    const url = this.buildUrl(`appmodules(${safeAppId})/appmoduleroles_association/$ref`);
    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, {
        method: "POST",
        headers,
        body: JSON.stringify({ "@odata.id": roleUri }),
      });
      await this.handleResponse<unknown>(response, `Associate role with app ${safeAppId}`);
    }, `Associate role with app ${safeAppId}`);
  }

  async disassociateSecurityRole(appId: string, roleId: string): Promise<void> {
    const safeAppId = validateRowId(appId);
    const safeRoleId = validateRowId(roleId);
    const headers = await this.getAuthHeaders();
    const roleUri = this.buildUrl(`roles(${safeRoleId})`);
    const url = this.buildUrl(
      `appmodules(${safeAppId})/appmoduleroles_association/$ref`,
      `$id=${encodeURIComponent(roleUri)}`,
    );
    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "DELETE", headers });
      await this.handleResponse<unknown>(response, `Disassociate role from app ${safeAppId}`);
    }, `Disassociate role from app ${safeAppId}`);
  }
}
